<script setup lang="ts">
import type { DesignModel } from '@forgd/contract/types'
import DtComparableProjectSelect from '#design-tokenomics/components/DtComparableProjectSelect.vue'
import { useNumberFieldBlurGuard } from '#design-tokenomics/composables/useNumberFieldBlurGuard'
import { useDesignDashboard } from '#design-tokenomics/composables/useDesignDashboard'
import { referenceProjectIDs } from '#design-tokenomics/utils/config'
import { formatValue } from '@forgd/formatter'
import { Field, FieldContent, FieldLabel } from '@forgd/ui-kit/blocks/field'
import { Card, CardContent, CardHeader, CardTitle } from '@forgd/ui-kit/card'
import { InputGroup, InputGroupAddon } from '@forgd/ui-kit/input-group'
import { NumberField, NumberFieldInput } from '@forgd/ui-kit/number-field'
import { Slider, SliderField, SliderValue } from '@forgd/ui-kit/slider'
import { Tooltip, TooltipContent, TooltipTrigger } from '@forgd/ui-kit/tooltip'
import { QuestionMarkCircleIcon } from '@heroicons/vue/24/outline'
import { useQuery } from '@pinia/colada'
import { isEqual } from 'ohash/utils'

const model = defineModel<DesignModel>({ required: true })

const { dashboard } = useDesignDashboard(model)

const id = useId()

const wholeNumberFormat = { maximumFractionDigits: 0 }
const wholeNumberGuard = useNumberFieldBlurGuard(wholeNumberFormat)

type ReferenceProject = SuccessResponseBody<'/coins-with-market-data'>[number]

const { data: marketProjects } = useQuery({
  ...buildApiQuery('/coins-with-market-data', {
    params: {
      query: {
        coingeckoIds: referenceProjectIDs.join(','),
      },
    },
  }),
})

const referenceProjects = computed(() =>
  marketProjects.value?.map(project => ({
    ...project,
    ticker: project.symbol,
  })),
)

const comparableSlots = ref<Array<ReferenceProject | undefined>>([undefined, undefined, undefined])
const slotsReady = ref(false)

watch(
  referenceProjects,
  (projects) => {
    if (!projects?.length || slotsReady.value) {
      return
    }

    comparableSlots.value = [0, 1, 2].map((index) => {
      const saved = model.value.comparables.comparables[index]
      if (!saved) {
        return undefined
      }

      return projects.find(project => project.name === saved.name)
        ?? {
          id: saved.name,
          name: saved.name,
          symbol: saved.name,
          image: '',
          price: 0,
          FDV: saved.FDV,
          MC: saved.MC,
        } satisfies ReferenceProject
    })
    slotsReady.value = true
  },
  { immediate: true },
)

watch(
  comparableSlots,
  (slots) => {
    if (!slotsReady.value) {
      return
    }

    const next = slots
      .filter((project): project is ReferenceProject => Boolean(project))
      .map(project => ({
        name: project.name,
        FDV: project.FDV,
        MC: project.MC,
      }))

    const current = model.value.comparables.comparables

    const sameSelection = isEqual(
      current.map(project => project.name),
      next.map(project => project.name),
    )

    if (sameSelection) {
      return
    }

    model.value = {
      ...model.value,
      comparables: {
        ...model.value.comparables,
        comparables: next,
      },
    }
  },
  { deep: true },
)

const selectedComparables = computed(() =>
  comparableSlots.value.filter((project): project is ReferenceProject => Boolean(project)),
)

const averageFdv = computed(() => {
  if (!selectedComparables.value.length) {
    return 0
  }

  return selectedComparables.value.reduce((sum, project) => sum + (project.FDV ?? 0), 0)
    / selectedComparables.value.length
})

const averageMarketCap = computed(() => {
  if (!selectedComparables.value.length) {
    return 0
  }

  return selectedComparables.value.reduce((sum, project) => sum + (project.MC ?? 0), 0)
    / selectedComparables.value.length
})

function patchConfigurations(changes: Partial<DesignModel['comparables']['configurations']>) {
  const configurations = { ...model.value.comparables.configurations, ...changes }

  const mirrored: Partial<Pick<DesignModel, 'FDV' | 'MTS'>> = {}

  if ('FDV' in changes) {
    mirrored.FDV = configurations.FDV
  }

  if ('MTS' in changes) {
    mirrored.MTS = configurations.MTS
  }

  model.value = {
    ...model.value,
    ...mirrored,
    comparables: { ...model.value.comparables, configurations },
  }
}

const achievePercentage = computed({
  get: () => model.value.comparables.configurations.achieveFDVPercentage ?? 75,
  set: (value: number) => patchConfigurations({
    achieveFDVPercentage: value,
    projectedFDV: (value / 100) * averageFdv.value,
  }),
})

const projectedFdv = computed(() => (achievePercentage.value / 100) * averageFdv.value)

watch(averageFdv, (avg) => {
  if (!slotsReady.value) {
    return
  }

  const next = (achievePercentage.value / 100) * avg
  if (model.value.comparables.configurations.projectedFDV === next) {
    return
  }

  patchConfigurations({ projectedFDV: next })
})

const fdv = computed({
  get: () => model.value.FDV,
  set: (value: number) => patchConfigurations({ FDV: value }),
})

const mts = computed({
  get: () => model.value.MTS,
  set: (value: number) => patchConfigurations({ MTS: value }),
})

const priceAtTge = computed(() => (mts.value > 0 ? fdv.value / mts.value : 0))

const circulatingSupplyAtTge = computed(() => {
  const emission = dashboard.value?.tokenomicsModeling.emission

  if (emission?.circulatingSupplyAtTGE != null) {
    return emission.circulatingSupplyAtTGE
  }

  const unlocked = emission?.percentageUnlockedAtTGE
  return unlocked ? mts.value * (unlocked / 100) : 0
})

const marketCapAtTge = computed(() => priceAtTge.value * circulatingSupplyAtTge.value)

const atTgeCards = computed(() => [
  {
    title: 'Token price at TGE',
    subtitle: 'Fully diluted valuation divided by maximum token supply.',
    value: priceAtTge.value,
  },
  {
    title: 'Market Capitalization at TGE',
    subtitle: 'Your valuation at TGE (based on token price * circulating supply)',
    value: marketCapAtTge.value,
  },
])

function clearSlot(index: number) {
  comparableSlots.value[index] = undefined
}
</script>

<template>
  <div class="space-y-5">
    <div class="flex w-full items-center justify-between">
      <div class="text-sm/normal font-semibold">
        1. Select comparable projects
      </div>
    </div>

    <div class="grid grid-cols-3 gap-5 text-sm @max-3xl:grid-cols-1">
      <DtComparableProjectSelect
        v-model="comparableSlots[0]"
        @trash="clearSlot(0)"
      />
      <DtComparableProjectSelect
        v-model="comparableSlots[1]"
        @trash="clearSlot(1)"
      />
      <DtComparableProjectSelect
        v-model="comparableSlots[2]"
        @trash="clearSlot(2)"
      />
    </div>

    <div class="flex flex-wrap gap-8">
      <div>
        <div class="text-xs text-gray-600">
          Average FDV
        </div>
        <div class="text-sm font-semibold">
          {{ formatValue(averageFdv, 'currency') }}
        </div>
      </div>
      <div>
        <div class="text-xs text-gray-600">
          Average Market Cap
        </div>
        <div class="text-sm font-semibold">
          {{ formatValue(averageMarketCap, 'currency') }}
        </div>
      </div>
    </div>

    <Card>
      <CardHeader>
        <CardTitle>2. Additional configurations</CardTitle>
      </CardHeader>
      <CardContent>
        <div class="grid grid-cols-2 gap-10 @max-3xl:grid-cols-1 @max-3xl:gap-5">
          <div class="space-y-5">
            <Field orientation="horizontal">
              <div class="flex items-center gap-1 md:min-h-13 md:w-1/2 md:shrink-0">
                <FieldLabel>Likelihood to achieve FDV</FieldLabel>
                <Tooltip>
                  <TooltipTrigger type="button" aria-label="More details" class="inline-flex">
                    <QuestionMarkCircleIcon class="size-4 shrink-0 text-primary-300" aria-hidden="true" />
                  </TooltipTrigger>
                  <TooltipContent>Some projects might be bigger than yours but still have a similar trajectory. Define here how much of the comparable projects FDV is attainable for you.</TooltipContent>
                </Tooltip>
              </div>
              <FieldContent>
                <SliderField size="lg">
                  <Slider
                    :model-value="[achievePercentage]"
                    :min="0"
                    :max="100"
                    :step="1"
                    data-testid="dt-achieve-fdv"
                    @update:model-value="(value) => value && (achievePercentage = value[0]!)"
                  />
                  <SliderValue>
                    {{ formatValue(achievePercentage, 'percent') }}
                  </SliderValue>
                </SliderField>
              </FieldContent>
            </Field>

            <Field orientation="horizontal">
              <div class="flex items-center gap-1 md:min-h-13 md:w-1/2 md:shrink-0">
                <FieldLabel :for="`${id}-projected-fdv`">
                  Projected FDV
                </FieldLabel>
                <Tooltip>
                  <TooltipTrigger type="button" aria-label="More details" class="inline-flex">
                    <QuestionMarkCircleIcon class="size-4 shrink-0 text-primary-300" aria-hidden="true" />
                  </TooltipTrigger>
                  <TooltipContent>Based on your likelihood to achieve a comparable projects FDV, this is the FDV we suggest for your project.</TooltipContent>
                </Tooltip>
              </div>
              <FieldContent>
                <NumberField
                  :model-value="projectedFdv"
                  :format-options="{ maximumFractionDigits: 0 }"
                  :step-snapping="false"
                  disabled
                >
                  <InputGroup size="lg">
                    <InputGroupAddon>$</InputGroupAddon>
                    <NumberFieldInput :id="`${id}-projected-fdv`" />
                  </InputGroup>
                </NumberField>
              </FieldContent>
            </Field>

            <Field orientation="horizontal">
              <div class="flex items-center gap-1 md:min-h-13 md:w-1/2 md:shrink-0">
                <FieldLabel :for="`${id}-fdv`">
                  Fully diluted valuation
                </FieldLabel>
                <Tooltip>
                  <TooltipTrigger type="button" aria-label="More details" class="inline-flex">
                    <QuestionMarkCircleIcon class="size-4 shrink-0 text-primary-300" aria-hidden="true" />
                  </TooltipTrigger>
                  <TooltipContent>Project valuation if the Maximum Token Supply is in circulation. This figure is calculated by multiplying 'Token Price' by 'Maximum Token Supply' and is generally denominated in US Dollars.</TooltipContent>
                </Tooltip>
              </div>
              <FieldContent>
                <NumberField
                  :model-value="fdv"
                  :min="0"
                  :format-options="wholeNumberFormat"
                  :step-snapping="false"
                  @update:model-value="wholeNumberGuard.accepts($event, fdv) && (fdv = Number.isFinite($event) ? $event : 0)"
                >
                  <InputGroup size="lg">
                    <InputGroupAddon>$</InputGroupAddon>
                    <NumberFieldInput v-bind="wholeNumberGuard.inputListeners" :id="`${id}-fdv`" data-testid="dt-adjust-fdv" />
                  </InputGroup>
                </NumberField>
              </FieldContent>
            </Field>

            <Field orientation="horizontal">
              <div class="flex items-center gap-1 md:min-h-13 md:w-1/2 md:shrink-0">
                <FieldLabel :for="`${id}-mts`">
                  Maximum token supply
                </FieldLabel>
                <Tooltip>
                  <TooltipTrigger type="button" aria-label="More details" class="inline-flex">
                    <QuestionMarkCircleIcon class="size-4 shrink-0 text-primary-300" aria-hidden="true" />
                  </TooltipTrigger>
                  <TooltipContent>The maximum number of tokens is coded to exist in the cryptocurrency's lifetime. The project determines this figure, which is always denominated in tokens.</TooltipContent>
                </Tooltip>
              </div>
              <FieldContent>
                <NumberField
                  :model-value="mts"
                  :min="0"
                  :format-options="wholeNumberFormat"
                  :step-snapping="false"
                  @update:model-value="wholeNumberGuard.accepts($event, mts) && (mts = Number.isFinite($event) ? $event : 0)"
                >
                  <InputGroup size="lg">
                    <NumberFieldInput v-bind="wholeNumberGuard.inputListeners" :id="`${id}-mts`" data-testid="dt-adjust-mts" />
                  </InputGroup>
                </NumberField>
              </FieldContent>
            </Field>
          </div>

          <div class="space-y-2">
            <div
              v-for="card in atTgeCards"
              :key="card.title"
              class="rounded-lg bg-neutral-200 p-5"
            >
              <div class="text-sm font-semibold">
                {{ card.title }}
              </div>
              <div class="text-sm text-gray-600">
                {{ card.subtitle }}
              </div>
              <div class="mt-2 text-2xl font-semibold">
                {{ formatValue(card.value, 'currency') }}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
</template>
